import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { ApiError } from '@crave/api-client';
import { RELEASE } from '@crave/shared';
import { api } from '../api.ts';

/**
 * "Something wrong?" from the person using Crave (#61). The app sends where they were, the release
 * and the POS number with it, so nobody types a version number.
 */
const KINDS: [string, string][] = [
  ['problem', 'Something is wrong'],
  ['idea', 'An idea'],
  ['question', 'A question'],
];

const CSS = `
.fb-open { border: 0; background: none; color: rgba(255,255,255,.75); font: inherit; font-size: 15px;
  font-weight: 600; min-height: 40px; padding: 0 8px; cursor: pointer; }
.fb-sheet { border: 0; padding: 0; margin: auto; width: min(520px, calc(100vw - 24px)); border-radius: 22px;
  background: var(--ivory); color: var(--ink); box-shadow: 0 24px 60px rgba(20,20,20,.3); }
.fb-sheet::backdrop { background: rgba(20,20,20,.5); }
.fb-body { display: grid; gap: 16px; padding: 22px; }
.fb-body h2 { margin: 0; font-size: 22px; }
.fb-kinds { display: flex; gap: 8px; flex-wrap: wrap; }
.fb-kind { min-height: 44px; padding: 0 14px; border-radius: 12px; border: 1px solid var(--line);
  background: var(--paper); font: inherit; font-weight: 600; cursor: pointer; }
.fb-kind[aria-pressed='true'] { background: var(--ink); border-color: var(--ink); color: #fff; }
.fb-text { min-height: 120px; padding: 12px 14px; border-radius: 14px; border: 1px solid var(--line);
  font: inherit; font-size: 17px; background: var(--paper); color: var(--ink); resize: vertical; }
.fb-note { margin: 0; color: var(--ink-soft); font-size: 14px; line-height: 1.45; }
.fb-actions { display: flex; gap: 10px; justify-content: flex-end; }
.fb-btn { min-height: 52px; padding: 0 20px; border: 0; border-radius: 14px; font: inherit; font-size: 17px;
  font-weight: 700; background: var(--crave-red); color: #fff; cursor: pointer; }
.fb-btn[data-kind='plain'] { background: none; color: var(--ink-soft); }
.fb-btn:disabled { opacity: .5; cursor: default; }
`;

export function SendFeedback({
  location,
  posNumber,
}: {
  location: string;
  posNumber?: number | null;
}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('problem');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const ref = useRef<HTMLDialogElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  const send = useMutation({
    mutationFn: async () =>
      api.post('/staff/feedback', {
        message: message.trim(),
        kind,
        app: 'kitchen',
        path: pathname,
        release: RELEASE,
        location,
      }),
    onSuccess: () => {
      setSent(true);
      setMessage('');
    },
  });

  const close = () => {
    setOpen(false);
    setSent(false);
    send.reset();
  };

  return (
    <>
      <style href="crave-send-feedback" precedence="default">
        {CSS}
      </style>
      <button type="button" className="fb-open" onClick={() => setOpen(true)}>
        Feedback
      </button>
      <dialog
        ref={ref}
        className="fb-sheet"
        aria-label="Send feedback"
        onCancel={(e) => {
          e.preventDefault();
          close();
        }}
      >
        {open && (
          <form
            className="fb-body"
            onSubmit={(e) => {
              e.preventDefault();
              if (message.trim()) send.mutate();
            }}
          >
            <h2>{sent ? 'Thank you' : 'Tell us what happened'}</h2>
            {sent ? (
              <>
                <p className="fb-note">
                  It went to the owner with the screen you were on, the release
                  {posNumber ? ' and this POS' : ''}, so nobody has to ask you for details.
                </p>
                <div className="fb-actions">
                  <button type="button" className="fb-btn" onClick={close}>
                    Done
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="fb-kinds">
                  {KINDS.map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className="fb-kind"
                      aria-pressed={kind === value}
                      onClick={() => setKind(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <textarea
                  className="fb-text"
                  value={message}
                  autoFocus
                  maxLength={2000}
                  placeholder="What happened, and what you expected."
                  onChange={(e) => setMessage(e.target.value)}
                />
                <p className="fb-note">
                  Sent with this screen, Crave {RELEASE}
                  {posNumber ? `, POS ${posNumber}` : ''} and your name.
                </p>
                {send.isError && (
                  <p className="fb-note" style={{ color: 'var(--crave-red)', fontWeight: 600 }}>
                    {send.error instanceof ApiError
                      ? send.error.message
                      : 'It did not send. Check the connection.'}
                  </p>
                )}
                <div className="fb-actions">
                  <button type="button" className="fb-btn" data-kind="plain" onClick={close}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="fb-btn"
                    disabled={!message.trim() || send.isPending}
                  >
                    {send.isPending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </>
            )}
          </form>
        )}
      </dialog>
    </>
  );
}
